import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog, Input } from '@/shared/ui';
import { useToast } from '@/shared/ui/Toast';
import { AgentChip } from '@/modules/agents/components/AgentChip';
import { CommsPreferenceCard } from '@/modules/agents/components/comms/CommsPreferenceCard';
import { skillLabel } from '@/modules/agents/components/labels';
import { SearchTermsPicker } from '@/modules/seo/components/SearchTermsPicker';
import { SEARCH_TERMS_DESCRIPTION } from '@/modules/seo/utils/searchTermsCopy';
import type { AgentDayOneReport, AgentSourceHealth } from '@/modules/agents/types';
import type { AgentIdentity } from '@/modules/agents/utils/agentLabels';
import type { CommunicationPreferencePatch } from '@/modules/agents/utils/commsLabels';
import {
  cardSummary,
  formatReportDate,
  reportParagraphs,
} from '@/modules/agents/utils/dayOneReport';
import { shouldLoadMissionSnapshot } from '@/modules/agents/utils/commandCenterPolling';
import {
  fixableByLabel,
  onboardingDisplay,
  requirementDetails,
} from '@/modules/agents/utils/onboardingDisplay';
import { resetLocations } from '@/modules/agents/utils/resetConfirm';
import {
  runNowMark,
  runNowResult,
  type RunNowMark,
} from '@/modules/agents/utils/agentStatusLabels';
import {
  composeSourcesChecked,
  hasNoConfiguredContextFeeds,
  sourceCheckedStateText,
  type SourceCheckedRow,
} from '@/modules/agents/utils/sourcesChecked';
import { type AgentWorkSchedule, type PracticeAgentRow } from '../../api/commandCenterApi';
import type { AgencyAgentStatus } from '../../api/agentStatusTypes';
import { commsApi } from '../../api/commsApi';
import { overridesApi } from '../../api/overridesApi';
import { registryApi } from '../../api/registryApi';
import { rolloutApi } from '../../api/rolloutApi';
import {
  useAgentStatus,
  useLocationDayOneReport,
  usePracticeAgents,
  usePracticeMission,
  useRunLocationAgent,
  useSetLocationAgent,
  useUnassignLocationAgent,
  useUpdateAgentSchedule,
  useWaiveAgentSchedule,
  usePauseAgentSchedule,
  useRunAgentSchedule,
} from '../../hooks/useCommandCenter';
import { usePractice } from '../../hooks/usePractices';
import {
  agencyKeys,
  commandCenterKeys,
  commsKeys,
  overrideKeys,
  registryKeys,
  rolloutKeys,
} from '../../queryKeys';
import { expiryLabel } from '../../utils/textDiff';
import { AgentStatusLine } from '../agentStatus/AgentStatusLine';
import { AgentTasks } from '../agentStatus/AgentTasks';
import { RunLogPanel } from '../agentStatus/RunLogPanel';
import {
  FACTS_PARAM,
  OBSERVATIONS_PARAM,
  RUN_LOG_PARAM,
  RUN_PARAM,
} from '../agentStatus/runLogPath';
import { ObservationsPanel } from '../agentStatus/ObservationsPanel';
import { ClientFactsPanel } from '../agentStatus/ClientFactsPanel';
import { ResetRehireButton } from './ResetRehireButton';
import { agencyApi } from '../../api/agencyApi';
import { practiceFeatureFlagKeys } from '@/shared/hooks/usePracticeFeatureFlags';
import {
  DiffModal,
  NewOverrideModal,
  PromoteModal,
  type OverrideRow,
} from '../registry/OverrideModals';
import { overrideErrorMessage } from '../registry/overrideErrorMessage';
import styles from './PracticeAgentsTab.module.css';

function fmtDate(value: string | number | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Why a schedule's Run now is unavailable, in words, or null when it can run. The server refuses the
 * same cases; saying so here saves a click that would only come back as "nothing started".
 */
function runNowBlocker(schedule: AgentWorkSchedule, paused: boolean): string | null {
  if (paused) return 'Resume the agent to run this';
  if (schedule.pausedAt) return 'Paused: switch it back on to run';
  switch (schedule.activeTaskState) {
    case 'awaiting_approval':
    case 'needs_review':
      return 'Waiting on the current one: approve, reject or skip it first';
    case 'ready':
    case 'in_progress':
      return 'Already queued or running';
    case 'blocked':
      return 'Blocked: see its task';
    default:
      return schedule.waitingUntil
        ? `Already queued, deferred until ${fmtDate(schedule.waitingUntil)}`
        : null;
  }
}

function fmtDateTime(value: string | number | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function apiErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response
    ?.data?.error?.message;
  return message ?? fallback;
}

function Switch({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className={styles.switch}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        aria-checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={styles.slider} />
    </label>
  );
}

/** The original tabbed task view on the practice's Tasks page, in place of the default conversation
 *  view (flag `classicAgentTasks`). */
function ClassicTaskViewToggle({ practiceId, enabled }: { practiceId: string; enabled: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (next: boolean) =>
      agencyApi.updatePracticeFlags(practiceId, { classicAgentTasks: next }),
    onSuccess: (_res, next) => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId) });
      queryClient.invalidateQueries({ queryKey: practiceFeatureFlagKeys.flags(practiceId) });
      toast.success(
        next
          ? 'Classic task view on: tasks open with tabs for this practice.'
          : 'Classic task view off: tasks open as conversations for this practice.',
      );
    },
    onError: () => toast.error('Could not update the flag — try again.'),
  });
  return (
    <div className={styles.previewToggle}>
      <div className={styles.previewText}>
        <span className={styles.previewLabel}>Classic task view</span>
        <span className={styles.previewHint}>Tabs instead of conversations on the Tasks page</span>
      </div>
      <Switch
        checked={enabled}
        disabled={mutation.isPending}
        label="Classic task view on the Tasks page"
        onChange={(next) => mutation.mutate(next)}
      />
    </div>
  );
}

function Section({
  title,
  hint,
  actions,
  children,
}: {
  title: string;
  hint?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles.section} aria-label={title}>
      <div className={styles.sectionHead}>
        <div>
          <h3 className={styles.sectionTitle}>{title}</h3>
          {hint && <p className={styles.sectionHint}>{hint}</p>}
        </div>
        {actions && <div className={styles.sectionActions}>{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/**
 * Everything an agency operator configures about an agent for ONE practice location. One entry in
 * the picker per (location, role) row; the right-hand side is that agent's status, tasks, work
 * schedules and sources, day one report, search terms, skill overrides and communication.
 *
 * Every persona and role name comes off the row (agent-suite's role.json/personas via the backend);
 * nothing here is written for a particular agent.
 */
export function PracticeAgentsTab({
  practiceId,
  locationId,
}: {
  practiceId: string;
  locationId: string;
}) {
  const { data, isLoading } = usePracticeAgents(practiceId);
  const { data: practice } = usePractice(practiceId);
  const rows = useMemo(
    () => (data ?? []).filter((row) => row.locationId === locationId),
    [data, locationId],
  );
  // A run log link (`runLog=<roleKey>`) opens on that agent.
  const [searchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState<string | null>(
    searchParams.get(RUN_LOG_PARAM) ??
      searchParams.get(FACTS_PARAM) ??
      searchParams.get(OBSERVATIONS_PARAM),
  );
  const selected = rows.find((row) => row.roleKey === selectedRole) ?? rows[0];

  // The whole-location reset sits under the agent list, apart from any one agent: it wipes and
  // re-hires every active agent here. Each agent's own panel resets only that agent.
  const locationReset = resetLocations(rows)[0];

  if (isLoading) return <div className={styles.empty}>Loading agents…</div>;
  if (rows.length === 0 || !selected) {
    return <div className={styles.empty}>This location has no agents yet.</div>;
  }

  return (
    <div className={styles.layout}>
      <div className={styles.pickerColumn}>
        <nav className={styles.picker} aria-label="Agents at this location">
          {rows.map((row) => {
            const active = row.roleKey === selected.roleKey;
            return (
              <button
                key={row.roleKey}
                type="button"
                className={clsx(styles.pickerItem, active && styles.pickerItemActive)}
                aria-current={active ? 'true' : undefined}
                onClick={() => setSelectedRole(row.roleKey)}
              >
                <span className={styles.pickerName}>
                  {row.agentName}
                  {row.assignment && (
                    <HiredDot
                      practiceId={practiceId}
                      row={row}
                      paused={row.assignment.status === 'paused'}
                    />
                  )}
                  {!row.assignment && row.tasksEnabled && (
                    <span
                      className={styles.unassignedDot}
                      aria-label="Switched on but not assigned here"
                      title="Switched on but not assigned here"
                    />
                  )}
                </span>
                <span className={styles.pickerRole}>{row.roleDisplayName}</span>
              </button>
            );
          })}
        </nav>
        {locationReset && (
          <ResetRehireButton
            practiceId={practiceId}
            location={locationReset}
            label="Reset all agents here…"
            variant="ghost"
          />
        )}
        {/* Practice-wide, not per agent: which task view the practice sees on its Tasks page. */}
        {practice && (
          <ClassicTaskViewToggle
            practiceId={practiceId}
            enabled={practice.featureFlags?.classicAgentTasks === true}
          />
        )}
      </div>

      <AgentDetail
        key={`${selected.locationId}:${selected.roleKey}`}
        practiceId={practiceId}
        row={selected}
        rowsAtLocation={rows}
      />
    </div>
  );
}

/** The hired dot beside an agent's name. Hidden while the agent's status is unknown (agent-suite unreachable), so it never says "hired and fine" when nobody knows. */
function HiredDot({
  practiceId,
  row,
  paused,
}: {
  practiceId: string;
  row: PracticeAgentRow;
  paused: boolean;
}) {
  const status = useAgentStatus(practiceId, row.locationId, row.roleKey, false, false);
  if (status.data?.state === 'unknown') return null;
  const label = paused ? 'Hired, paused' : 'Hired';
  return (
    <span
      className={clsx(styles.hiredDot, paused && styles.hiredDotPaused)}
      aria-label={label}
      title={label}
    />
  );
}

/**
 * One agent's sections, and what links them: the task a reason points at, and the run log panel.
 * The panel's state is in the URL (`runLog=<roleKey>&run=<id>`) so the full page can collapse back
 * to it.
 */
function AgentDetail({
  practiceId,
  row,
  rowsAtLocation,
}: {
  practiceId: string;
  row: PracticeAgentRow;
  rowsAtLocation: PracticeAgentRow[];
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusTaskId, setFocusTaskId] = useState<string | null>(null);
  const status = useAgentStatus(practiceId, row.locationId, row.roleKey);
  const runLogOpen = searchParams.get(RUN_LOG_PARAM) === row.roleKey;
  const focusRunId = runLogOpen ? (searchParams.get(RUN_PARAM) ?? undefined) : undefined;
  const openRunLog = useCallback(
    (runId?: string) =>
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(RUN_LOG_PARAM, row.roleKey);
          if (runId) next.set(RUN_PARAM, runId);
          else next.delete(RUN_PARAM);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams, row.roleKey],
  );
  const factsOpen = searchParams.get(FACTS_PARAM) === row.roleKey;
  const observationsOpen = searchParams.get(OBSERVATIONS_PARAM) === row.roleKey;
  // Each side panel's open state is a URL param naming this agent, so it survives a reload.
  const setPanelOpen = useCallback(
    (param: string, openIt: boolean) =>
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (openIt) next.set(param, row.roleKey);
          else next.delete(param);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams, row.roleKey],
  );
  const closeRunLog = useCallback(
    () =>
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(RUN_LOG_PARAM);
          next.delete(RUN_PARAM);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  return (
    <div className={styles.sections}>
      <StatusSection
        practiceId={practiceId}
        row={row}
        rowsAtLocation={rowsAtLocation}
        status={status.data}
        statusLoading={status.isLoading}
        onOpenRunLog={openRunLog}
        onOpenClientFacts={() => setPanelOpen(FACTS_PARAM, true)}
        onOpenObservations={() => setPanelOpen(OBSERVATIONS_PARAM, true)}
        onFocusTask={setFocusTaskId}
      />
      <Section title="Tasks">
        <AgentTasks
          practiceId={practiceId}
          locationId={row.locationId}
          roleKey={row.roleKey}
          agentName={row.agentName}
          focusTaskId={focusTaskId}
          onOpenRun={openRunLog}
        />
      </Section>
      <WorkSchedulesSection practiceId={practiceId} row={row} />
      <SourcesSection practiceId={practiceId} row={row} />
      <DayOneSection practiceId={practiceId} row={row} />
      {needsSearchTerms(row) && <SearchTermsSection practiceId={practiceId} row={row} />}
      <SkillOverridesSection practiceId={practiceId} row={row} />
      <CommunicationSection practiceId={practiceId} row={row} />
      <RunLogPanel
        open={runLogOpen}
        onClose={closeRunLog}
        practiceId={practiceId}
        locationId={row.locationId}
        roleKey={row.roleKey}
        agentName={row.agentName}
        focusRunId={focusRunId}
      />
      <ClientFactsPanel
        open={factsOpen}
        onClose={() => setPanelOpen(FACTS_PARAM, false)}
        practiceId={practiceId}
        locationId={row.locationId}
        locationName={row.locationName}
      />
      <ObservationsPanel
        open={observationsOpen}
        onClose={() => setPanelOpen(OBSERVATIONS_PARAM, false)}
        practiceId={practiceId}
        locationId={row.locationId}
        roleKey={row.roleKey}
        agentName={row.agentName}
        locationName={row.locationName}
      />
    </div>
  );
}

// ─── Status ────────────────────────────────────────────────────────────────

const RUN_NOW_POLL_MS = 4_000;
const RUN_NOW_WATCH_MS = 5 * 60_000;

function StatusSection({
  practiceId,
  row,
  rowsAtLocation,
  status,
  statusLoading,
  onOpenRunLog,
  onOpenClientFacts,
  onOpenObservations,
  onFocusTask,
}: {
  practiceId: string;
  row: PracticeAgentRow;
  rowsAtLocation: PracticeAgentRow[];
  status: AgencyAgentStatus | undefined;
  statusLoading: boolean;
  onOpenRunLog: (runLogId?: string) => void;
  onOpenClientFacts: () => void;
  onOpenObservations: () => void;
  onFocusTask: (taskId: string) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const setAgent = useSetLocationAgent(practiceId);
  const unassign = useUnassignLocationAgent(practiceId);
  const run = useRunLocationAgent(practiceId);
  const assigned = !!row.assignment;
  const paused = row.assignment?.status === 'paused';
  const setup = onboardingDisplay(assigned, row.onboarding);
  const metDetails = assigned ? requirementDetails(row.onboarding) : [];
  const busy = setAgent.isPending || unassign.isPending || run.isPending;
  // What the reset re-hires: every ACTIVE agent at this location (the backend applies the same
  // rule). A paused agent is assigned but not re-hired, so the dialog names only the active ones.
  const resetLocation = useMemo(
    () =>
      resetLocations(rowsAtLocation).find((location) => location.locationId === row.locationId) ?? {
        locationId: row.locationId,
        locationName: row.locationName,
        agentNames: [row.agentName],
      },
    [rowsAtLocation, row.locationId, row.locationName, row.agentName],
  );

  const tasksFlag = useMutation({
    mutationFn: (enabled: boolean) =>
      rolloutApi.setAgentTasksFlag(practiceId, row.roleKey, enabled),
    onSuccess: (_result, enabled) => {
      queryClient.invalidateQueries({ queryKey: commandCenterKeys.practiceAgents(practiceId) });
      queryClient.invalidateQueries({ queryKey: rolloutKeys.all });
      toast.success(
        enabled
          ? `The practice can now see ${row.agentName}'s tasks`
          : `${row.agentName}'s tasks are hidden from the practice`,
      );
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not update the flag')),
  });
  // Internal-only agents can only be switched on for practices marked internal — the flag would
  // be rejected, so the switch says why instead of failing.
  const flagLocked =
    (row.rolloutStage === 'internal_only' && !row.practiceInternal) ||
    (!!row.hireBlocker && !row.tasksEnabled);
  // Joe (community) cannot be hired while the Community module is off: the server rejects it, so
  // the switch is disabled with the hint instead of failing.
  const hireLocked = !!row.hireBlocker && !assigned;

  const toggleAssigned = async (next: boolean) => {
    try {
      if (next) {
        await setAgent.mutateAsync({
          locationId: row.locationId,
          roleKey: row.roleKey,
        });
        toast.success(`${row.agentName} assigned to ${row.locationName}`);
      } else {
        await unassign.mutateAsync({ locationId: row.locationId, roleKey: row.roleKey });
        toast.success(`${row.agentName} unassigned from ${row.locationName}`);
      }
    } catch (err) {
      // The hire gate ("Add the location website before hiring Sam") arrives here — show it verbatim,
      // it tells the operator exactly what to fix.
      toast.error(err instanceof Error ? err.message : 'Could not update the assignment');
    }
  };

  const togglePaused = async () => {
    if (!row.assignment) return;
    const next = paused ? 'active' : 'paused';
    try {
      await setAgent.mutateAsync({
        locationId: row.locationId,
        roleKey: row.roleKey,
        status: next,
      });
      toast.success(
        next === 'paused'
          ? `${row.agentName} paused at ${row.locationName}`
          : `${row.agentName} resumed at ${row.locationName}`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the assignment');
    }
  };

  // After "Run now", follow the status until it shows what the click led to: a run that started, or
  // one that ended (a wake that found nothing logs "Nothing to do" and why). Joe's scan can take a
  // few minutes before it wakes him, so the watch lasts up to RUN_NOW_WATCH_MS.
  const [watch, setWatch] = useState<{ mark: RunNowMark; until: number } | null>(null);
  useEffect(() => {
    if (!watch) return;
    const result = runNowResult(watch.mark, status?.run, row.agentName);
    if (result) {
      if (result.tone === 'error') toast.error(result.text);
      else if (result.tone === 'success') toast.success(result.text);
      else toast.info(result.text);
      setWatch(null);
    }
  }, [watch, status, row.agentName, toast]);
  useEffect(() => {
    if (!watch) return;
    const timer = window.setInterval(() => {
      if (Date.now() > watch.until) {
        setWatch(null);
        return;
      }
      queryClient.invalidateQueries({
        queryKey: commandCenterKeys.agentStatus(practiceId, row.locationId, row.roleKey, false),
      });
    }, RUN_NOW_POLL_MS);
    return () => window.clearInterval(timer);
  }, [watch, queryClient, practiceId, row.locationId, row.roleKey]);

  const runNow = async () => {
    try {
      const mark = runNowMark(status?.run);
      const result = await run.mutateAsync({ locationId: row.locationId, roleKey: row.roleKey });
      // A refusal is an expected outcome, not a failure — most often a double-click.
      if (result.started) {
        toast.success(
          result.message ?? `${row.agentName} started the checks for ${row.locationName}`,
        );
        setWatch({ mark, until: Date.now() + RUN_NOW_WATCH_MS });
      } else toast.info(result.reason ?? 'No new check started');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start a run');
    }
  };

  return (
    <Section
      title="Status"
      hint={`Each kind of work runs on its own schedule. Turning ${row.agentName} off at a location stops that work and closes anything still open. Past tasks and what ${row.agentName} learned are kept.`}
      actions={
        <>
          <Button
            variant="secondary"
            size="sm"
            // Deliberately does NOT pre-guess "is a run already in progress" from the status —
            // agent-suite's own gate decides when the click lands, and a decline comes back as
            // { started: false, reason } (an info toast). 'unknown' still disables: that is
            // agent-suite being unreachable, so the request would fail outright.
            disabled={!assigned || busy || status?.state === 'unknown'}
            onClick={runNow}
          >
            {run.isPending ? 'Starting…' : 'Run now'}
          </Button>
          {assigned && (
            <Button variant="secondary" size="sm" disabled={busy} onClick={togglePaused}>
              {paused ? 'Resume' : 'Pause'}
            </Button>
          )}
          <ResetRehireButton
            practiceId={practiceId}
            location={resetLocation}
            agent={{ roleKey: row.roleKey, agentName: row.agentName }}
            label={`Reset & rehire ${row.agentName}…`}
            disabled={!assigned || paused || busy}
          />
        </>
      }
    >
      <AgentStatusLine
        status={status}
        isLoading={statusLoading}
        agentName={row.agentName}
        onOpenRunLog={onOpenRunLog}
        onOpenClientFacts={onOpenClientFacts}
        onOpenObservations={onOpenObservations}
        onFocusTask={onFocusTask}
      />
      <div className={styles.cards}>
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.cardLabel}>Hired at this location</span>
            <Switch
              checked={assigned}
              disabled={
                busy ||
                hireLocked ||
                (row.rolloutStage === 'internal_only' && !row.practiceInternal && !assigned)
              }
              label={`Assign ${row.agentName} to ${row.locationName}`}
              onChange={toggleAssigned}
            />
          </div>
          <div className={styles.cardBody}>
            {assigned ? (
              paused ? (
                `${row.agentName} is hired but paused. No work runs until you press Resume.`
              ) : (
                `${row.agentName} is working here. Each kind of work runs on its own schedule (see Work schedules below).`
              )
            ) : hireLocked ? (
              <>
                {row.hireBlocker}:{' '}
                <Link to={`/agency/practices/${practiceId}?tab=modules&module=community`}>
                  Modules › Community
                </Link>
                .
              </>
            ) : row.rolloutStage === 'internal_only' && !row.practiceInternal ? (
              <>
                {row.agentName} is internal-only: mark this practice internal under{' '}
                <Link to={`/agency/practices/${practiceId}?tab=settings`}>Settings › Profile</Link>,
                then turn on this switch.
              </>
            ) : (
              `${row.agentName} isn't working at ${row.locationName}. Turn on this switch to start ${row.agentName} here.`
            )}
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.cardLabel}>Practice can see tasks</span>
            <Switch
              checked={row.tasksEnabled}
              disabled={flagLocked || tasksFlag.isPending}
              label={`Let the practice see ${row.agentName}'s tasks`}
              onChange={(next) => tasksFlag.mutate(next)}
            />
          </div>
          <div className={styles.cardBody}>
            <span title={`Flag: ${row.tasksFlag}`}>
              {row.tasksEnabled
                ? `The practice can see ${row.agentName}'s tasks.`
                : `The practice can't see ${row.agentName}'s tasks.`}
            </span>
            {row.hireBlocker && !row.tasksEnabled ? (
              <p className={styles.cardNote}>
                {row.hireBlocker}:{' '}
                <Link to={`/agency/practices/${practiceId}?tab=modules&module=community`}>
                  Modules › Community
                </Link>
                .
              </p>
            ) : flagLocked ? (
              <p className={styles.cardNote}>
                {row.agentName} is internal-only: mark this practice internal under{' '}
                <Link to={`/agency/practices/${practiceId}?tab=settings`}>Settings › Profile</Link>,
                then switch this on.
              </p>
            ) : row.tasksEnabled && !assigned ? (
              // The flag is practice-wide but hiring is per location, so the flag can be on while
              // nothing works here. Stays until the agent is assigned (or the flag goes off).
              <p className={styles.cardWarning} role="status">
                The practice can see {row.agentName}&apos;s tasks, but {row.agentName} isn&apos;t
                working at {row.locationName} yet, so there&apos;s nothing to show here. To start{' '}
                {row.agentName} here, turn on <strong>Hired at this location</strong>.
              </p>
            ) : (
              !row.tasksEnabled && (
                <p className={styles.cardNote}>
                  {assigned
                    ? `${row.agentName} is working, but the practice won't see the tasks until you switch this on.`
                    : `Hiring ${row.agentName} doesn't show the tasks to the practice. Switch this on when you're ready.`}
                </p>
              )
            )}
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.cardLabel}>Setup</span>
          </div>
          <div className={styles.cardBody}>
            {/* A separate axis from the hired switch and the agent's state: whether the setup
                behind this location is actually complete. Naming what is missing is what tells an
                operator whether it is theirs to fix or the practice's. */}
            {setup.kind === 'unknown' && (
              // The backend could not answer for this agent. Drawing nothing here would look exactly
              // like a finished setup.
              <div className={styles.notReady}>
                <span className={styles.setupUnknownPill}>Setup unknown</span>
              </div>
            )}
            {setup.kind === 'not_ready' && (
              <div className={styles.notReady}>
                <span className={styles.notReadyPill}>
                  Not ready · {setup.satisfied}/{setup.total}
                </span>
                {setup.missing.map((requirement) => {
                  const fixer = fixableByLabel(requirement.fixableBy);
                  return (
                    <span key={requirement.key} className={styles.notReadyItem}>
                      {requirement.label}
                      {fixer && <span className={styles.notReadyFixer}> ({fixer})</span>}
                      {requirement.detail && (
                        <span className={styles.requirementDetail}>{requirement.detail}</span>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
            {setup.kind === 'none' && (
              <span className={styles.cardMuted}>{assigned ? 'Setup complete' : 'Not hired'}</span>
            )}
            {/* A met requirement can still carry a line worth reading, e.g. each ad platform's
                state. Shown so "ready" is something the operator can see, not take on trust. */}
            {metDetails.map((requirement) => (
              <span key={requirement.key} className={styles.requirementDetail}>
                {requirement.detail}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

// ─── Work schedules ────────────────────────────────────────────────────────

/** Skill name, then the skill's check/quota row ahead of its event rows, then event name. */
function compareSchedules(left: AgentWorkSchedule, right: AgentWorkSchedule): number {
  return (
    left.label.localeCompare(right.label) ||
    Number(left.triggerType === 'event') - Number(right.triggerType === 'event') ||
    (left.event ?? left.triggerKey).localeCompare(right.event ?? right.triggerKey)
  );
}

type ScheduleTaskState = NonNullable<AgentWorkSchedule['activeTaskState']>;

const SCHEDULE_TASK_STATE: Record<ScheduleTaskState, { label: string; className: string }> = {
  ready: { label: 'Queued', className: styles.stateRunning },
  in_progress: { label: 'Running', className: styles.stateRunning },
  awaiting_approval: { label: 'Awaiting approval', className: styles.stateAwaiting },
  needs_review: { label: 'Needs review', className: styles.stateReview },
  blocked: { label: 'Blocked', className: styles.stateUnknown },
  failed: { label: 'Failed', className: styles.stateUnknown },
  // A closed task should not stay pinned to a schedule; if one does, it reads as idle.
  done: { label: 'Scheduled', className: styles.stateIdle },
  cancelled: { label: 'Scheduled', className: styles.stateIdle },
};

/** The schedule's status pill plus the one line under it that says what happens next. */
function scheduleStatus(schedule: AgentWorkSchedule): {
  label: string;
  className: string;
  detail: string;
} {
  const next = schedule.dueAt
    ? `Next ${fmtDate(schedule.dueAt)}`
    : schedule.triggerType === 'event'
      ? 'When source data changes'
      : 'Not scheduled';
  if (schedule.pausedAt) {
    return {
      label: 'Paused',
      className: styles.stateIdle,
      detail: `Since ${fmtDate(schedule.pausedAt)}${
        schedule.activeTaskState
          ? ` · finishing: ${SCHEDULE_TASK_STATE[schedule.activeTaskState].label.toLowerCase()}`
          : ''
      }`,
    };
  }
  if (schedule.activeTaskState) {
    return {
      ...SCHEDULE_TASK_STATE[schedule.activeTaskState],
      detail: "Open task — nothing new starts until it's closed",
    };
  }
  if (schedule.waitingUntil) {
    return {
      label: 'Queued',
      className: styles.stateRunning,
      detail: `Deferred until ${fmtDate(schedule.waitingUntil)}`,
    };
  }
  return { label: 'Scheduled', className: styles.stateIdle, detail: next };
}

function WorkSchedulesSection({ practiceId, row }: { practiceId: string; row: PracticeAgentRow }) {
  const toast = useToast();
  const updateSchedule = useUpdateAgentSchedule(practiceId);
  const waiveSchedule = useWaiveAgentSchedule(practiceId);
  const runSchedule = useRunAgentSchedule(practiceId);
  const pauseSchedule = usePauseAgentSchedule(practiceId);
  const schedules = useMemo(() => [...row.schedules].sort(compareSchedules), [row.schedules]);
  const [waiving, setWaiving] = useState<AgentWorkSchedule | null>(null);
  const [waiveReason, setWaiveReason] = useState('');
  // What is waiting on the server, per control ('run:<scheduleId>', 'pause:<skillKey>', ...), so a
  // click only locks the control it came from — not every button in the table.
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const assigned = !!row.assignment;
  const paused = row.assignment?.status === 'paused';

  const track = async (key: string, work: () => Promise<void>) => {
    setPending((current) => new Set(current).add(key));
    try {
      await work();
    } finally {
      setPending((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    }
  };

  const changeSchedule = (schedule: AgentWorkSchedule, value: string) =>
    track(`frequency:${schedule.id}`, async () => {
      try {
        const reset = value === 'default';
        await updateSchedule.mutateAsync({
          locationId: row.locationId,
          roleKey: row.roleKey,
          scheduleId: schedule.id,
          override:
            schedule.triggerType === 'check'
              ? { intervalDays: reset ? null : Number(value) }
              : { quotaCount: reset ? null : Number(value) },
        });
        toast.success(`${schedule.label} schedule updated`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not update the work schedule');
      }
    });

  // Start this schedule's next piece of work now instead of at its slot. A refusal (a draft still
  // waiting for approval, work already in progress) comes back as started:false with a reason.
  const runNow = (schedule: AgentWorkSchedule) =>
    track(`run:${schedule.id}`, async () => {
      try {
        const result = await runSchedule.mutateAsync({
          locationId: row.locationId,
          roleKey: row.roleKey,
          scheduleId: schedule.id,
        });
        if (result.started) toast.success(`${row.agentName} started: ${schedule.label}`);
        else toast.info(result.reason ?? 'Nothing new was started');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not start this work');
      }
    });

  // Pausing is per skill: an audit's check and event rows pause and resume together. Work already
  // open finishes; nothing new starts until it is resumed.
  // Keyed by skill, because that is the unit that pauses: every row of the skill waits together.
  const setSchedulePaused = (schedule: AgentWorkSchedule, pause: boolean) =>
    track(`pause:${schedule.skillKey}`, async () => {
      try {
        await pauseSchedule.mutateAsync({
          locationId: row.locationId,
          roleKey: row.roleKey,
          scheduleId: schedule.id,
          paused: pause,
        });
        toast.success(`${schedule.label} ${pause ? 'paused' : 'resumed'}`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not change this schedule');
      }
    });

  const closeWaive = () => {
    setWaiving(null);
    setWaiveReason('');
  };

  const waiveSlot = async () => {
    const reason = waiveReason.trim();
    if (!waiving || !reason) return;
    try {
      await waiveSchedule.mutateAsync({
        locationId: row.locationId,
        roleKey: row.roleKey,
        scheduleId: waiving.id,
        reason,
      });
      toast.success(`${waiving.label} slot waived`);
      closeWaive();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not waive the schedule slot');
    }
  };

  return (
    <Section title="Work schedules">
      {row.schedules.length === 0 && (
        <div className={styles.quiet}>
          {assigned ? 'Unavailable' : `No schedules until ${row.agentName} is hired.`}
        </div>
      )}
      {schedules.length > 0 && (
        <div className={styles.tableScroll}>
          <div className={styles.scheduleTable} role="table" aria-label="Work schedules">
            <div className={styles.scheduleHeader} role="row">
              <span role="columnheader">Skill</span>
              <span role="columnheader">Schedule</span>
              <span role="columnheader">Status</span>
              <span
                role="columnheader"
                title="When a slot last finished: approved, reviewed or skipped"
              >
                Last completed
              </span>
              <span
                role="columnheader"
                title="Off stops new work of this kind here. Work already open finishes."
              >
                Active
              </span>
            </div>
            {schedules.map((schedule) => {
              const status = scheduleStatus(schedule);
              const blocker = runNowBlocker(schedule, paused);
              const canWaive =
                schedule.triggerType === 'quota' &&
                ['blocked', 'awaiting_approval', 'needs_review'].includes(
                  schedule.activeTaskState ?? '',
                );
              return (
                <div className={styles.scheduleRow} role="row" key={schedule.id}>
                  <span className={styles.cellStack} role="cell">
                    <span className={styles.scheduleName}>{schedule.label}</span>
                    <code className={styles.mono}>{schedule.skillKey}</code>
                  </span>
                  <span className={styles.scheduleCell} role="cell">
                    <ScheduleFrequency
                      schedule={schedule}
                      disabled={pending.has(`frequency:${schedule.id}`)}
                      onChange={(value) => changeSchedule(schedule, value)}
                    />
                    {schedule.triggerType !== 'event' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={!assigned || !!blocker || pending.has(`run:${schedule.id}`)}
                        title={blocker ?? 'Start the next slot now instead of waiting for it'}
                        onClick={() => runNow(schedule)}
                      >
                        {pending.has(`run:${schedule.id}`) ? 'Starting…' : 'Run now'}
                      </Button>
                    )}
                    {schedule.newerData && (
                      <span className={styles.newerData} role="status">
                        Newer data is available (as of {schedule.newerData.asOfLocal}). Run a fresh
                        report to see it.
                      </span>
                    )}
                  </span>
                  <span className={styles.cellStack} role="cell">
                    <span className={clsx(styles.statePill, status.className)}>{status.label}</span>
                    <span className={styles.cellSub}>{status.detail}</span>
                    {canWaive && (
                      <button
                        type="button"
                        className={styles.linkButton}
                        title="Skip this slot without doing the work"
                        onClick={() => setWaiving(schedule)}
                      >
                        Waive this slot
                      </button>
                    )}
                  </span>
                  <span className={styles.cellMuted} role="cell">
                    {schedule.lastCompletedAt ? fmtDate(schedule.lastCompletedAt) : 'Never'}
                  </span>
                  <span role="cell">
                    <Switch
                      checked={!schedule.pausedAt}
                      disabled={!assigned || pending.has(`pause:${schedule.skillKey}`)}
                      label={`${schedule.label} active`}
                      onChange={(active) => setSchedulePaused(schedule, !active)}
                    />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={!!waiving}
        onClose={closeWaive}
        onConfirm={waiveSlot}
        title={waiving ? `Waive this ${waiving.label} slot?` : 'Waive this slot?'}
        message={
          <Input
            label="Reason"
            fullWidth
            autoFocus
            value={waiveReason}
            placeholder="Why this slot is being skipped"
            onChange={(event) => setWaiveReason(event.target.value)}
          />
        }
        confirmLabel="Waive slot"
        isLoading={waiveSchedule.isPending}
        confirmDisabled={!waiveReason.trim()}
      />
    </Section>
  );
}

/** The schedule's cadence as an override picker when the skill allows one, plain text otherwise. */
function ScheduleFrequency({
  schedule,
  disabled,
  onChange,
}: {
  schedule: AgentWorkSchedule;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  if (
    schedule.triggerType === 'check' &&
    schedule.minIntervalDays !== null &&
    schedule.maxIntervalDays !== null
  ) {
    const min = schedule.minIntervalDays;
    const max = schedule.maxIntervalDays;
    const choices = [min, 7, 14]
      .filter((days, index, all) => days >= min && days < max && all.indexOf(days) === index)
      .sort((left, right) => left - right);
    return (
      <select
        className={styles.scheduleControl}
        aria-label={`${schedule.label} frequency`}
        disabled={disabled}
        value={schedule.intervalDaysOverride ?? 'default'}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="default">Every {max} days (default)</option>
        {choices.map((days) => (
          <option key={days} value={days}>
            Every {days} days
          </option>
        ))}
      </select>
    );
  }
  if (schedule.triggerType === 'quota' && schedule.minimumQuotaCount !== null) {
    const min = schedule.minimumQuotaCount;
    const period = schedule.quotaPeriod ?? 'period';
    return (
      <select
        className={styles.scheduleControl}
        aria-label={`${schedule.label} quota`}
        disabled={disabled}
        value={schedule.quotaCountOverride ?? 'default'}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="default">
          {min} per {period} (default)
        </option>
        {Array.from({ length: 4 }, (_, index) => min + index + 1).map((count) => (
          <option key={count} value={count}>
            {count} per {period}
          </option>
        ))}
      </select>
    );
  }
  if (schedule.triggerType === 'event') {
    return (
      <span className={styles.cellStack}>
        {schedule.eventLabel && <span className={styles.cellMuted}>{schedule.eventLabel}</span>}
        <span className={styles.cellMuted}>
          {!schedule.eventLabel && 'On '}
          <code className={styles.mono}>{schedule.event ?? schedule.triggerKey}</code>
        </span>
      </span>
    );
  }
  return <span className={styles.cellMuted}>{schedule.frequency}</span>;
}

// ─── Day one report ───────────────────────────────────────────────────────────

function DayOneReport({ report, agentName }: { report: AgentDayOneReport; agentName: string }) {
  const [open, setOpen] = useState(false);
  const summary = cardSummary(report);
  return (
    <section className={styles.report} aria-label={`${agentName}'s first look`}>
      <span className={styles.reportKicker}>Day one report</span>
      <h4 className={styles.reportTitle}>{report.headline}</h4>
      <span className={styles.reportMeta}>{formatReportDate(report.createdAt)}</span>
      {report.topItems.length > 0 ? (
        <ol className={styles.reportItems}>
          {report.topItems.map((title, index) => (
            <li key={`${index}-${title}`}>{title}</li>
          ))}
        </ol>
      ) : (
        summary && <p className={styles.reportSummary}>{summary}</p>
      )}
      <button
        type="button"
        className={styles.reportToggle}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        {open ? 'Hide the full report' : 'Read the full report'}
      </button>
      {open && (
        <div className={styles.reportBody}>
          {reportParagraphs(report.body).map((paragraph, index) => (
            <p key={index} className={styles.reportParagraph}>
              {paragraph}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Search terms ─────────────────────────────────────────────────────────────

/** Shown for any role that declares the `search_terms` requirement in its role.json, so this stays
 *  data-driven. `onboarding` is null when nothing is assigned (or agent-suite couldn't be reached);
 *  the setting still has to be editable then, because saving it is what unblocks the hire. */
function needsSearchTerms(row: PracticeAgentRow): boolean {
  if (row.onboarding) return row.onboarding.requirements.some((r) => r.key === 'search_terms');
  return row.roleKey === 'seo';
}

function SearchTermsSection({ practiceId, row }: { practiceId: string; row: PracticeAgentRow }) {
  const queryClient = useQueryClient();
  return (
    <Section title="Search terms" hint={SEARCH_TERMS_DESCRIPTION}>
      <SearchTermsPicker
        key={row.locationId}
        locationId={row.locationId}
        practiceId={practiceId}
        // The requirement's `satisfied` flag lives on the agents row — refresh it after a save.
        onSaved={() =>
          queryClient.invalidateQueries({ queryKey: commandCenterKeys.practiceAgents(practiceId) })
        }
      />
    </Section>
  );
}

function DayOneSection({ practiceId, row }: { practiceId: string; row: PracticeAgentRow }) {
  const report = useLocationDayOneReport(practiceId, row.locationId);
  // The report is written once per location and stamped with the role that wrote it; another
  // agent's first look does not belong under this agent.
  const ownReport =
    report.data && (!report.data.roleKey || report.data.roleKey === row.roleKey)
      ? report.data
      : null;
  if (!ownReport) return null;
  return (
    <Section title="Day one report">
      <DayOneReport report={ownReport} agentName={row.agentName} />
    </Section>
  );
}

// ─── Skill overrides ───────────────────────────────────────────────────────

function SkillOverridesSection({ practiceId, row }: { practiceId: string; row: PracticeAgentRow }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [diffRow, setDiffRow] = useState<OverrideRow | null>(null);
  const [promoteRow, setPromoteRow] = useState<OverrideRow | null>(null);
  const now = Date.now();

  const practice = usePractice(practiceId);
  const overrides = useQuery({
    queryKey: overrideKeys.location(practiceId, row.locationId),
    queryFn: () => overridesApi.listForLocation(practiceId, row.locationId),
  });
  const registry = useQuery({
    queryKey: registryKeys.registry(),
    queryFn: () => registryApi.getRegistry(),
    staleTime: 5 * 60_000,
  });
  // Promote opens a pull request against the skill registry's repo — the same backend write-back
  // config (GITHUB_REPO and friends) AgencySkillOverridesPage.tsx gates its own Promote button on.
  // Without it configured, Promote would only fail after the modal's own round trip.
  const writeBack = useQuery({
    queryKey: registryKeys.writeBack(),
    queryFn: () => registryApi.getWriteBack(),
    staleTime: 5 * 60_000,
  });
  const writeBackConfigured = writeBack.data?.configured ?? false;
  const agents = useMemo<AgentIdentity[]>(
    () =>
      (registry.data?.roles ?? []).map((role) => ({
        roleKey: role.key,
        personaName: role.persona?.name ?? role.displayName,
        displayName: role.displayName,
      })),
    [registry.data],
  );
  const agentFor = useMemo(
    () => new Map((registry.data?.skills ?? []).map((skill) => [skill.key, skill.agentType])),
    [registry.data],
  );

  const rows = useMemo<OverrideRow[]>(() => {
    const practiceName = practice.data?.name ?? '';
    return (overrides.data ?? [])
      .filter((override) => agentFor.get(override.skillKey) === row.roleKey)
      .map((override) => ({
        ...override,
        location: { id: row.locationId, name: row.locationName },
        practice: { id: practiceId, name: practiceName },
      }));
  }, [
    overrides.data,
    agentFor,
    row.roleKey,
    row.locationId,
    row.locationName,
    practiceId,
    practice.data?.name,
  ]);

  const retire = useMutation({
    mutationFn: (override: OverrideRow) =>
      overridesApi.retire(
        override.practice.id,
        override.location.id,
        override.id,
        'retired from the Command Center',
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overrideKeys.all });
      toast.success('Override retired. The global skill applies from the next run.');
    },
    onError: (err) => toast.error(overrideErrorMessage(err)),
  });

  return (
    <Section
      title="Skill overrides"
      hint={`Practice-specific versions of ${row.agentName}'s skills at ${row.locationName}. Time-boxed, and only what the agent reads changes.`}
      actions={
        <Button size="sm" variant="secondary" onClick={() => setCreating(true)}>
          + New override
        </Button>
      }
    >
      {overrides.isLoading && <div className={styles.quiet}>Loading overrides…</div>}
      {overrides.isError && <div className={styles.quiet}>Overrides could not load.</div>}
      {overrides.data && !registry.isLoading && rows.length === 0 && (
        <div className={styles.quiet}>
          No active overrides. {row.agentName} runs the global skills here.
        </div>
      )}
      {rows.length > 0 && (
        <div className={styles.overrideTable} role="table" aria-label="Skill overrides">
          <div className={styles.overrideHeader} role="row">
            <span role="columnheader">Skill</span>
            <span role="columnheader">Changes</span>
            <span role="columnheader">Reason</span>
            <span role="columnheader">Expiry</span>
            <span role="columnheader" />
          </div>
          {rows.map((override) => {
            const expiry = expiryLabel(override.expiresAt, now);
            const expiryClass =
              expiry.tone === 'ok'
                ? styles.expiryOk
                : expiry.tone === 'soon'
                  ? styles.expirySoon
                  : styles.expiryReview;
            return (
              <div className={styles.overrideRow} role="row" key={override.id}>
                <span className={styles.overrideSkill} role="cell">
                  <span className={styles.overrideSkillName}>{skillLabel(override.skillKey)}</span>
                  <span className={styles.overrideSub}>
                    <AgentChip
                      agentType={agentFor.get(override.skillKey)}
                      agents={agents}
                      compact
                    />{' '}
                    <code className={styles.mono}>{override.skillKey}</code>
                  </span>
                </span>
                <span className={styles.overrideSub} role="cell">
                  {[
                    override.method !== undefined && 'method',
                    override.references !== undefined && 'references',
                    override.objective !== undefined && 'objective',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                <span className={styles.overrideReason} role="cell">
                  {override.reason}
                </span>
                <span role="cell">
                  <span className={expiryClass}>{expiry.text}</span>
                </span>
                <span className={styles.overrideActions} role="cell">
                  <Button size="sm" variant="secondary" onClick={() => setDiffRow(override)}>
                    Diff
                  </Button>
                  <span
                    title={
                      writeBackConfigured
                        ? undefined
                        : 'Write-back is not configured on the backend — this screen is read-only until it is (GITHUB_REPO and friends).'
                    }
                  >
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!writeBackConfigured}
                      onClick={() => setPromoteRow(override)}
                    >
                      Promote
                    </Button>
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={retire.isPending}
                    onClick={() => retire.mutate(override)}
                  >
                    Retire
                  </Button>
                </span>
              </div>
            );
          })}
        </div>
      )}
      <NewOverrideModal
        open={creating}
        onClose={() => setCreating(false)}
        agents={agents}
        defaultPracticeId={practiceId}
        defaultLocationId={row.locationId}
        defaultAgentType={row.roleKey}
      />
      <DiffModal row={diffRow} onClose={() => setDiffRow(null)} />
      <PromoteModal
        row={promoteRow}
        onClose={() => setPromoteRow(null)}
        onDone={(change) => {
          setPromoteRow(null);
          queryClient.invalidateQueries({ queryKey: registryKeys.all });
          toast.success(
            `Pull request #${change.prNumber} opened. Retire the override once it merges.`,
          );
        }}
      />
    </Section>
  );
}

// ─── Communication ─────────────────────────────────────────────────────────

function CommunicationSection({ practiceId, row }: { practiceId: string; row: PracticeAgentRow }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const prefs = useQuery({
    queryKey: commsKeys.practice(practiceId),
    queryFn: () => commsApi.list(practiceId),
  });
  const save = useMutation({
    mutationFn: (args: { agentType: string; patch: CommunicationPreferencePatch }) =>
      commsApi.update(practiceId, args.agentType, args.patch),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: commsKeys.practice(practiceId) });
      toast.success(
        `${saved.personaName}'s preferences saved. The practice sees the same settings.`,
      );
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not save — try again.')),
  });
  const preference = prefs.data?.find((pref) => pref.agentType === row.roleKey);

  return (
    <Section
      title="Communication"
      hint="The practice edits these same preferences under Settings → Notifications. Engagement mode and standing permissions appear only for an agent whose work can pause for a decision; the always-ask list can never be switched off from either side."
    >
      {prefs.isLoading && <div className={styles.quiet}>Loading preferences…</div>}
      {prefs.isError && (
        <div className={styles.quiet}>Preferences could not load. Try again in a moment.</div>
      )}
      {prefs.data && !preference && (
        <div className={styles.quiet}>
          No communication preferences exist for {row.agentName} yet.
        </div>
      )}
      {preference && (
        <CommsPreferenceCard
          key={preference.agentType}
          preference={preference}
          door="agency"
          saving={save.isPending && save.variables?.agentType === preference.agentType}
          onSave={(patch) => save.mutate({ agentType: preference.agentType, patch })}
        />
      )}
    </Section>
  );
}

// ─── Sources the agent checks ──────────────────────────────────────────────

const SOURCE_STATE_CLASS: Record<SourceCheckedRow['state'], string> = {
  current: styles.expiryOk,
  always_available: styles.expiryOk,
  never_checked: styles.stateIdle,
  not_checked: styles.stateIdle,
  delayed: styles.stateAwaiting,
  not_connected: styles.stateUnknown,
  error: styles.stateUnknown,
};

function SourcesSection({ practiceId, row }: { practiceId: string; row: PracticeAgentRow }) {
  // RF S5: only a row with an assignment has a mission to read — an unassigned row would just
  // poll every 15s for a snapshot that never has anything to show.
  const assigned = shouldLoadMissionSnapshot(row);
  const mission = usePracticeMission(
    assigned ? practiceId : null,
    assigned ? row.locationId : null,
    assigned ? row.roleKey : null,
  );
  const snapshot = mission.data;
  const sourcesChecked = useMemo(
    () =>
      composeSourcesChecked(snapshot?.sourceHealth ?? [], snapshot?.contextSourcesChecked ?? []),
    [snapshot?.sourceHealth, snapshot?.contextSourcesChecked],
  );
  const healthByKey = useMemo(
    () =>
      new Map<string, AgentSourceHealth>(
        (snapshot?.sourceHealth ?? []).map((health) => [health.sourceKey, health]),
      ),
    [snapshot?.sourceHealth],
  );
  const noFeedsConfigured = hasNoConfiguredContextFeeds(snapshot?.contextSourcesChecked ?? []);

  return (
    <Section title={`Sources ${row.agentName} checks`}>
      {!assigned && <div className={styles.quiet}>{row.agentName} is not hired here.</div>}
      {assigned && mission.isLoading && !snapshot && (
        <div className={styles.quiet}>Loading sources…</div>
      )}
      {assigned && !mission.isLoading && !snapshot && (
        <div className={styles.quiet}>Sources are unavailable.</div>
      )}
      {snapshot && (
        <>
          {sourcesChecked.length > 0 ? (
            <div className={styles.tableScroll}>
              <div className={styles.sourceTable} role="table" aria-label="Sources">
                <div className={styles.sourceHeader} role="row">
                  <span role="columnheader">Source</span>
                  <span role="columnheader">State</span>
                  <span role="columnheader">Last success</span>
                  <span role="columnheader" title="How recent the data from the last success is">
                    Data through
                  </span>
                  <span role="columnheader">Next check</span>
                </div>
                {sourcesChecked.map((source) => {
                  // Timestamps exist only for the business sources the backend polls; the calendar
                  // and context feeds have no check of their own.
                  const health = healthByKey.get(source.key);
                  return (
                    <div className={styles.sourceRow} role="row" key={source.key}>
                      <span className={styles.cellStack} role="cell">
                        <span className={styles.scheduleName}>
                          {source.kind === 'context_feed' ? (
                            // Untrusted, external text: rendered as quoted, attributed source
                            // content, never folded into the product's own sentence.
                            <cite>{source.label}</cite>
                          ) : (
                            source.label
                          )}
                        </span>
                        {health && <code className={styles.mono}>{health.sourceKey}</code>}
                      </span>
                      <span className={styles.cellStack} role="cell">
                        <span className={clsx(styles.statePill, SOURCE_STATE_CLASS[source.state])}>
                          {sourceCheckedStateText(source.state)}
                        </span>
                        {source.reasonCode && (
                          <code className={styles.mono}>{source.reasonCode}</code>
                        )}
                      </span>
                      <span className={styles.cellMuted} role="cell">
                        {fmtDateTime(health?.lastSuccessAt)}
                      </span>
                      <span className={styles.cellMuted} role="cell">
                        {fmtDateTime(health?.dataThroughAt)}
                      </span>
                      <span className={styles.cellMuted} role="cell">
                        {fmtDateTime(health?.nextAttemptAt)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className={styles.quiet}>No source checks are recorded for this location yet.</p>
          )}
          {snapshot.sourceHealth?.length === 0 && (
            <p className={styles.note}>
              None of the scheduled data pulls at this location wake {row.agentName}.{' '}
              {row.agentName} reads the data each task needs fresh when it runs.
            </p>
          )}
          {noFeedsConfigured && (
            <p className={styles.note}>
              No external news or current-events feeds are configured for this location yet.
            </p>
          )}
        </>
      )}
    </Section>
  );
}
